import type {
  ComparisonRow,
  FlowStage,
  InsightCopy,
  Plan,
  SolutionGroup,
} from '@/features/marketing/content';

/**
 * English marketing copy, translated from `locales/es/marketing.ts` (the
 * original). It is a *localization*, not a word-for-word rendering: trade-show
 * vocabulary differs between markets, so `stand` becomes `booth`, `recinto`
 * becomes `venue` and `eventos físicos` becomes `in-person events`.
 *
 * Prices are deliberately absent — they live in `features/marketing/content.ts`
 * so a tariff change can never land in one language and miss another.
 *
 * ⚠️ Pending review by the team: this copy was drafted alongside the i18n work
 * and has not been through a commercial read yet.
 */

export const marketing = {
  hero: {
    eyebrow: 'ILUMETRIA',
    titulo: 'The <g>Google Analytics</g> for in-person events',
    lead: 'We turn every interaction into a business opportunity.',
    apoyo:
      'The first data intelligence platform that illuminates the ROI of your in-person events with IoT and AI.',
    ctaPrimary: 'Book a demo',
    ctaSecondary: 'See solutions and plans',
    videoLabel: 'ILUMETRIA platform demo',
    videoFallback: 'Your browser cannot play this video.',
  },

  /**
   * LAS 100 PRO 2026 award, the footer announcement.
   *
   * The award keeps its Spanish name on purpose: it is a Mexican programme and
   * translating "LAS 100 PRO" would name something that does not exist.
   *
   * The date and the image are not in this file — they live in
   * `components/layout/datosDelAnuncio.ts` so they cannot drift between
   * languages. There was a sign-up link too; it was removed on 2026-09-21.
   */
  reconocimiento: {
    titulo: 'Recognised as one of LAS 100 PRO 2026',
    logoAlt: 'LAS 100 PRO 2026 badge',
    cuerpo:
      'News that makes us proud: LAS 100 PRO singles out the Mexican startups with the greatest potential for growth, innovation and impact. It tells us the problem we picked —measuring what actually happens inside an event— matters, and pushes us to take it to more venues and more brands.',
    premiacionEtiqueta: 'Award ceremony:',
    /** No letters on purpose: it separates the two ceremony details. */
    premiacionSeparador: ' · ',
    premiacionLugar: 'Teatro de la UDEM',
    gracias:
      'Thank you to PRO NETWORK for driving this initiative and to Hey Banco for making this seventh edition possible. And thank you to our team, our clients, and the partners, mentors and investors who backed this when it was only a hypothesis.',
  },

  insight: {
    eyebrow: 'The problem',
    headline: 'We close the blind spot of in-person events',
    question: 'Do you know what share of your visitors left without giving you their details?',
    answer:
      'We capture and qualify your passive leads using attendee profiles and the emotions we detect as they interact with your brand.',
    donutLabel: 'Passive leads',
    legendPassive: (pct: number) => `${pct}% passive leads (invisible today)`,
    legendScanned: (pct: number) => `${pct}% scanned leads`,
    chartAlt: (pct: number) =>
      `${pct}% of visitors are passive leads who leave no contact details today`,
  } satisfies InsightCopy & { eyebrow: string },

  flow: {
    eyebrow: 'Value flow',
    title: 'With you from start to finish',
    lead: 'Before, during and after the event: each stage feeds the next one with real data.',
    tablistLabel: 'Event stages',
  },

  flowStages: [
    {
      id: 'pre',
      step: '01',
      shortLabel: 'PRE',
      title: 'PRE-EVENT: Intelligent Matchmaking',
      image: '/recursos/matchmaking.png',
      imageAlt: 'Matchmaking screen pairing brands with events on the ILUMETRIA platform',
      blocks: [
        {
          title: 'For Brands',
          body: 'Our platform recommends the right events in your area for your brand, with ROI estimates based on each event reputation, attendee profiling and historical sentiment analysis.',
        },
        {
          title: 'For Organizers',
          body: 'Put your events in front of brands and let them bid to sponsor you — or set your own rates directly.',
        },
      ],
    },
    {
      id: 'durante',
      step: '02',
      shortLabel: 'DURING',
      title: 'DURING THE EVENT: IoT + AI at work',
      image: '/recursos/stand_iot.png',
      imageAlt: 'Booth fitted with a reader antenna and a computer vision module',
      blocks: [
        {
          title: 'Passive lead capture',
          body: 'We give every attendee a badge or wristband with a smart tag, and every booth a reader antenna with sub-meter precision.',
        },
        {
          title: 'Touch to Collect',
          body: 'Attendees exchange their details with a single NFC tap, becoming an active — and legally consented — lead.',
        },
        {
          title: 'Emotion analysis',
          body: 'Our AI reads attendee micro-expressions as they engage with your booth, so you can tell real interest from politeness.',
        },
      ],
      note: 'All our technology is Plug & Play, so you can forget slow, costly on-site installations with a technician.',
    },
    {
      id: 'post',
      step: '03',
      shortLabel: 'POST',
      title: 'POST-EVENT: The Attribution Dashboard',
      image: '/recursos/dashboard_post.png',
      imageAlt: 'Attribution dashboard showing post-event performance reports',
      blocks: [
        {
          title: 'Detailed reports',
          body: 'The platform hands you detailed reports on how your brand performed at the event, with context and heat maps, plus passive and active lead generation.',
        },
        {
          title: 'Smart recommendations',
          body: 'We tell you how to get more out of your next sponsorship, and list the upcoming events where you have the best odds of a high ROI.',
        },
      ],
    },
  ] satisfies FlowStage[],

  solutions: {
    eyebrow: 'Maximize your impact',
    title: 'Real results for organizers and brands',
  },

  solutionGroups: [
    {
      id: 'organizadores',
      audience: 'For Organizers',
      image: '/recursos/organizer_dashboard.png',
      imageAlt: 'Organizer dashboard with venue traffic and occupancy metrics',
      items: [
        {
          id: 'certificacion',
          title: 'Audience Certification',
          body: 'Turn your venue into a Smart Venue. Attract better sponsors with real-time traffic and occupancy figures.',
        },
        {
          id: 'multitudes',
          title: 'Crowd Management',
          body: 'See your bottlenecks and your cold zones. Gather metrics that are worth having for planning and safety.',
        },
        {
          id: 'retencion',
          title: 'Brand Retention',
          body: 'Prove with data that your event pays off, and keep your exhibitors investing year after year.',
        },
      ],
    },
    {
      id: 'marcas',
      audience: 'For Brands / Exhibitors',
      image: '/recursos/exhibitor_dashboard.png',
      imageAlt: 'Brand dashboard with captured leads and ROI attribution',
      items: [
        {
          id: 'roi',
          title: 'ROI Maximization',
          body: 'Invest with your eyes open. Our dashboard shows you which events will deliver the results you need.',
        },
        {
          id: 'leads-pasivos',
          title: 'Passive Lead Visibility',
          body: 'Get sharply defined audience profiles. Qualify prospects automatically from their emotional response.',
        },
        {
          id: 'atribucion',
          title: 'Direct Attribution',
          body: 'Connect every dollar you spend on the booth to warm leads delivered straight into your CRM.',
        },
      ],
    },
  ] satisfies SolutionGroup[],

  pricing: {
    eyebrow: 'Our plans',
    title: 'Choose your smart investment',
    lead: 'A subscription to find the right events, and per-event hardware to measure what happens inside them.',
    featuredTag: 'Most chosen',
    free: 'Free',
    /**
     * Prices are in Mexican pesos. Left bare, `$2,999.00` reads as US dollars
     * to an English speaker — a 20x misunderstanding on a pricing page, so the
     * currency is always spelled out here.
     */
    formatoPrecio: (precio: string) => `${precio} MXN`,
    freeNote: 'No cost',
    customNote:
      'Need something different? We tailor the infrastructure for events with more than 100 booths.',
    customCta: 'Request a custom plan',
  },

  plans: [
    {
      id: 'free-pass',
      name: 'Free Pass',
      ctaLabel: 'Create a free account',
      variants: [
        {
          id: 'marcas',
          label: 'Brands',
          priceNote: 'Free',
          features: [
            'Access to the event listing for your region',
            'Matchmaking for 1 event per month, with ROI analytics and attendee profiling',
            'Performance history limited to 2 events attended',
          ],
        },
        {
          id: 'organizadores',
          label: 'Organizers',
          priceNote: 'Free',
          features: [
            'Publish your events freely',
            'Matchmaking with up to 3 potential sponsors for 1 event per month, with ROI analytics and average-ticket recommendations per sponsor',
            'Performance history through the organizer portal, limited to 2 past events',
            'Event promotion on the platform',
          ],
        },
      ],
    },
    {
      id: 'smart-match',
      name: 'Smart Match Subscription',
      featured: true,
      ctaLabel: 'Subscribe',
      secondaryCtaLabel: 'Book a DEMO',
      variants: [
        {
          id: 'marcas',
          label: 'Brands',
          priceNote: 'Monthly cost',
          description:
            'For brands and exhibitors who want to be at the best events and maximize THEIR ROI.',
          features: [
            'Access to the event listing for your region',
            'Matchmaking for up to 10 events per month in your region, plus ROI analytics and attendee profiling for every event listed on the platform',
            'Your complete performance history across events attended',
            'Make custom sponsorship offers to the organizers of any event listed on the platform',
            'Hardware rental discounts based on how often you sponsor events',
          ],
        },
        {
          id: 'organizadores',
          label: 'Organizers',
          priceNote: 'Monthly cost',
          features: [
            'Publish your events freely',
            'A platform quality certificate based on attendee emotion analysis and ratings from previous sponsors',
            'Matchmaking with up to 10 potential sponsors across up to 5 events per month, with ROI analytics and average-ticket recommendations per sponsor',
            'Your complete performance history through the organizer portal',
            'Hardware rental discounts based on how often you sponsor events',
            'Event promotion on the platform, with discounts based on how often you organize',
          ],
        },
      ],
    },
    {
      id: 'smart-partner',
      name: 'Smart Partner Plan for Organizers',
      ctaLabel: 'Get started',
      secondaryCtaLabel: 'Book a DEMO',
      variants: [
        {
          id: 'un-dia',
          label: '1 day',
          priceNote: 'Cost for 1 day',
          description:
            'Our end-to-end solution for digitizing the whole venue. A model where the organizer recovers the investment and delivers real value to sponsors.',
          features: [
            'HaaS infrastructure: 5 sub-meter precision antennas and 5 computer vision modules, easily fitted to booths for passive lead capture and attendee emotion analysis. Each booth needs 1 antenna and 1 vision module; all our technology is Plug & Play for easy installation.',
            'A pack of 100 smart tags for your attendees (1 per attendee) with a quick-to-deploy data capture module at your event entrance',
            'Platform access for metric tracking and report generation, for up to 5 sponsors and 1 organizer',
          ],
        },
        {
          id: 'tres-dias',
          label: '3 days',
          priceNote: 'Cost for 3 days',
          description:
            'Our end-to-end solution for digitizing the whole venue. A model where the organizer recovers the investment and delivers real value to sponsors.',
          features: [
            'HaaS infrastructure: 5 sub-meter precision antennas and 5 computer vision modules, easily fitted to booths for passive lead capture and attendee emotion analysis. Each booth needs 1 antenna and 1 vision module; all our technology is Plug & Play for easy installation.',
            'A pack of 100 smart tags for your attendees (1 per attendee) with a quick-to-deploy data capture module at your event entrance',
            'Platform access for metric tracking and report generation, for up to 5 sponsors and 1 organizer',
          ],
        },
      ],
    },
    {
      id: 'solo-brand',
      name: 'Solo Brand Plan',
      ctaLabel: 'Get started',
      secondaryCtaLabel: 'Book a DEMO',
      variants: [
        {
          id: 'un-dia',
          label: '1 day',
          priceNote: 'Cost for 1 day',
          description:
            'Built for brands that want metrics from their event without depending on the organizer.',
          features: [
            'HaaS infrastructure: 2 computer vision modules, easily fitted to your booth, for counting potential passive leads and analyzing attendee emotions. You need 2 per booth; all our technology is Plug & Play for easy installation.',
            'Platform access for metric tracking and report generation for your brand',
          ],
        },
        {
          id: 'tres-dias',
          label: '3 days',
          priceNote: 'Cost for 3 days',
          description:
            'Built for brands that want metrics from their event without depending on the organizer.',
          features: [
            'HaaS infrastructure: 2 computer vision modules, easily fitted to your booth, for counting potential passive leads and analyzing attendee emotions. You need 2 per booth; all our technology is Plug & Play for easy installation.',
            'Platform access for metric tracking and report generation for your brand',
          ],
        },
      ],
    },
  ] satisfies Plan[],

  comparison: {
    eyebrow: 'Side by side',
    title: 'What each hardware plan measures',
    colFeature: 'Capability',
    colSoloBrand: 'Solo Brand Plan',
    colSmartPartner: 'Smart Partner Plan for Organizers',
  },

  comparisonRows: [
    {
      id: 'trafico',
      feature: 'Traffic count',
      soloBrand: 'Visual (estimated)',
      smartPartner: 'Exact (per individual)',
    },
    {
      id: 'dwell-time',
      feature: 'Dwell time',
      soloBrand: 'Limited to the camera area',
      smartPartner: 'Full traceability across the booth',
    },
    {
      id: 'emociones',
      feature: 'Emotion analysis',
      soloBrand: 'Included',
      smartPartner: 'Included',
    },
    {
      id: 'lead-pasivo',
      feature: 'Passive lead capture',
      soloBrand: 'Not available',
      smartPartner: 'Yes (Virtual Clone data)',
      soloBrandMissing: true,
    },
    {
      id: 'nfc',
      feature: 'Touch to Collect (NFC)',
      soloBrand: 'Not available',
      smartPartner: 'Yes (digital exchange)',
      soloBrandMissing: true,
    },
  ] satisfies ComparisonRow[],

  partners: {
    eyebrow: 'Industry approved',
    title: 'Brands betting on what comes next',
  },

  finalCta: {
    headline: 'Ready to illuminate your next event?',
    body: 'Join the brands already measuring success in the real world.',
    ctaLabel: 'Talk to our sales team',
  },
  /**
   * Study notice: a temporary notification on the home page.
   *
   * Switched off with `ESTUDIO_ABIERTO` in `features/estudio/campana.ts`. The
   * copy does not mention the product: it invites you to the study, not to the
   * platform.
   */
  avisoEstudio: {
    titulo: 'We are running an industry study',
    cuerpo:
      'Tell us how you measure your events and we will send you the full report with the results from across the sector.',
    cta: 'See what it is about',
    duracion: '6-8 min',
    cerrar: 'Dismiss',
  },

};
